// src/App.jsx
import React, { useEffect, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from "./context/AuthContext";

import { workspacesService } from './services/workspacesService';
import { userService } from './services/userService';
import MainDashboard from './components/Dashboard/MainDashboard';
import ChecklistenManager from './components/ChecklistenManager';
import ShoppingListsPage from './components/ShoppingList/ShoppingListsPage';
import UserCalendar from './components/Calendar/MainCalendar';
import HomeInventory from './components/HomeInventory';
import TopNav from './components/TopNav';
import TopNavLoggedIn from './components/TopNavLoggedIn';
import Essensplanung from './components/Meals/Essensplanung';
import PlmPage from './components/product_lifecycle_management/PlmPage';

import NotesManager from './components/Notes/Notes';
import TasksLayout from './components/Tasks/TasksLayout';
import SettingsManager from './components/Settings';
import ProfileAndWorkspaceManager from './components/Workspaces/ProfileAndWorkspace';
import AcceptInvitePage from './components/Workspaces/AcceptInvitePage';
import CookieConsentWithGA from './components/CookieConsentWithGA';
import useStore from './utils/store';

// IMPORTANT: I created a reusable EU footer component earlier in the canvas named "Eu Footer Component".
// Import it here. Adjust the path to where you put that file.
import AppFooter from './components/AppFooter';

// New pages
import Impressum from './components/Impressum';
import Kontakt from './components/Kontakt';
import Datenschutz from './components/Datenschutz';
import Header from './components/LandingPage/Header';
import HeroSection from './components/LandingPage/HeroSection';
import FeaturesSection from './components/LandingPage/FeatureSection';
import AISection from './components/LandingPage/AISection';
import HowItWorksSection from './components/LandingPage/HowItWorksSection';
import CTASection from './components/LandingPage/CTASection';
import PricingPage from './components/LandingPage/PricingPage';
import TermsAndConditionsOverlay from './components/TermsAndCondictionsOverlay';
import { config } from './config';

const App = () => {
  const auth = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [showTermsOverlay, setShowTermsOverlay] = useState(false);

  useEffect(() => {
    if (auth.isLoading) return;         // still loading
    if (!auth.isAuthenticated) {
      setLoading(false);
      return;  // user not logged in yet
    }
    if (!auth.user) {
      setLoading(false);
      return; // user object not ready
    }

    const loadWorkspaces = async () => {
      try {
        // Token is now handled by cookies
        // const token = auth.user.access_token; // REMOVED

        // 1. Fetch User Details to get last_active_workspace_id
        let lastActiveWorkspaceId = null;
        let userData = null;

        try {
          userData = await userService.getMe();
          if (userData) {
            lastActiveWorkspaceId = userData.last_active_workspace_id;

            // CHECK TERMS VERSION
            const currentTermsVersion = config.CURRENT_TERMS_VERSION;
            if (userData.agreed_terms_version !== currentTermsVersion) {
              setShowTermsOverlay(true);
            }
          }
        } catch (uErr) {
          console.error("Failed to fetch user details for workspace persistence", uErr);
        }

        // Fetch Subscription
        try {
          const subData = await userService.getSubscription();
          if (subData) {
            useStore.setState({ subscription: subData });
          }
        } catch (subErr) {
          console.error("Failed to fetch subscription", subErr);
        }

        // 2. Fetch All Workspaces
        const res = await workspacesService.getAll();
        const data = res.data || [];
        const savedId = localStorage.getItem("workspaceId");

        let workspaceId = null;
        if (data.length > 0) {
          // Priority: 1. Backend Persistence, 2. LocalStorage, 3. First Available
          const candidateId = lastActiveWorkspaceId || savedId;
          const found = candidateId ? data.find(w => String(w.id) === String(candidateId)) : null;

          if (found) {
            workspaceId = found.id;
          } else {
            workspaceId = data[0].id;
          }
        }

        if (workspaceId) {
          const activeWorkspace = data.find(w => w.id === workspaceId);
          useStore.setState({
            workspaceId,
            workspaceType: activeWorkspace?.type || 'home',
            workspaceName: activeWorkspace?.name || ''
          });
          // ensure localStorage holds the chosen id
          localStorage.setItem("workspaceId", workspaceId);

          // Sync back to backend if it was different or missing
          if (String(lastActiveWorkspaceId) !== String(workspaceId)) {
            userService.updateActiveWorkspace(workspaceId).catch(console.error);
          }
        }
      } catch (err) {
        console.error("Failed to fetch workspaces", err);
      } finally {
        setLoading(false);
      }
    };

    loadWorkspaces();
  }, [auth.isAuthenticated, auth.isLoading, auth.user]);

  const handleAcceptTerms = async () => {
    try {
      // Token implicitly handled
      await userService.acceptTerms(config.CURRENT_TERMS_VERSION);
      setShowTermsOverlay(false);
    } catch (error) {
      console.error("Failed to accept terms", error);
      alert("Failed to accept terms. Please try again.");
    }
  };

  // sign out behavior (keeps your original logic)
  // sign out behavior
  const signOutRedirect = () => {
    auth.logout();
  };

  if (auth.isLoading) {
    return <div className="text-center py-5">Loading...</div>;
  }

  if (auth.error) {
    const errorMsg = auth.error.message?.toLowerCase() || "";
    if (errorMsg.includes("failed to fetch") || errorMsg.includes("network error")) {
      auth.logout?.();
      return <Navigate to="/" replace />;
    }

    // For other errors, we might want to show them or also logout
    // keeping original behavior for non-fetch errors but ensuring logout availability
    if (errorMsg === "no matching state found in storage") {
      auth.logout?.();
      return <Navigate to="/" replace />;
    }

    // Default error display with logout option
    return (
      <div className="text-center py-5">
        <p>Encountering error... {auth.error.message}</p>
        <button className="btn btn-primary" onClick={() => auth.logout?.()}>Logout</button>
      </div>
    );
  }

  return (
    <div className="app-root">
      <CookieConsentWithGA />

      {auth.isAuthenticated ? (
        <>
          <TermsAndConditionsOverlay isVisible={showTermsOverlay} onAccept={handleAcceptTerms} />
          <TopNavLoggedIn auth={auth} signOutRedirect={signOutRedirect} />
        </>
      ) : (
        <Header auth={auth} />
      )}

      <main className="app-main" style={{ backgroundColor: "#ffffffff", color: "#333", display: "flex", flexDirection: "column" }}>
        {loading ? (
          <div className="text-center py-5">Loading...</div>
        ) : (
          <Routes>
            <Route path="/" element={auth.isAuthenticated ? <Navigate to="/dashboard" replace /> : (
              <>
                <HeroSection />
                <FeaturesSection />
                <AISection />
                <HowItWorksSection />
                <CTASection />
              </>
            )} />

            {/* Authenticated routes */}
            <Route path="/dashboard" element={auth.isAuthenticated ? <MainDashboard auth={auth} signOutRedirect={signOutRedirect} /> : <Navigate to="/" />} />
            <Route path="/join" element={<AcceptInvitePage auth={auth} />} />
            <Route path="/packlisten" element={auth.isAuthenticated ? <ChecklistenManager auth={auth} signOutRedirect={signOutRedirect} /> : <Navigate to="/" />} />
            <Route path="/kalender" element={auth.isAuthenticated ? <UserCalendar auth={auth} signOutRedirect={signOutRedirect} /> : <Navigate to="/" />} />
            <Route path="/Essensplanung" element={auth.isAuthenticated ? <Essensplanung auth={auth} signOutRedirect={signOutRedirect} /> : <Navigate to="/" />} />

            <Route path="/plm" element={auth.isAuthenticated ? <PlmPage auth={auth} signOutRedirect={signOutRedirect} /> : <Navigate to="/" />} />
            <Route path="/tasks" element={auth.isAuthenticated ? <TasksLayout auth={auth} signOutRedirect={signOutRedirect} /> : <Navigate to="/" />} />
            <Route path="/ShoppingList" element={auth.isAuthenticated ? <ShoppingListsPage auth={auth} signOutRedirect={signOutRedirect} /> : <Navigate to="/" />} />
            <Route path="/Notes" element={auth.isAuthenticated ? <NotesManager auth={auth} signOutRedirect={signOutRedirect} /> : <Navigate to="/" />} />
            <Route path="/Notes/:id" element={auth.isAuthenticated ? <NotesManager auth={auth} signOutRedirect={signOutRedirect} /> : <Navigate to="/" />} />
            <Route path="/Profile" element={auth.isAuthenticated ? <ProfileAndWorkspaceManager auth={auth} signOutRedirect={signOutRedirect} /> : <Navigate to="/" />} />
            <Route path="/Settings" element={auth.isAuthenticated ? <SettingsManager auth={auth} signOutRedirect={signOutRedirect} /> : <Navigate to="/" />} />

            {/* Public / Legal pages */}
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/impressum" element={<Impressum />} />
            <Route path="/kontakt" element={<Kontakt />} />
            <Route path="/datenschutz" element={<Datenschutz />} />

            <Route path="*" element={<div className="text-center py-5">Seite nicht gefunden.</div>} />
          </Routes>
        )}
      </main>

      <AppFooter />

    </div>
  );

};

export default App;
